<template>
  <div class="lines-tab">
    <div class="lines-tab-header">
      <div class="lines-counts">
        <span class="count-chip"
          >{{ localLines.length }} line{{
            localLines.length !== 1 ? "s" : ""
          }}</span
        >
        <span class="count-chip"
          >{{ uniqueProductCount }} product{{
            uniqueProductCount !== 1 ? "s" : ""
          }}</span
        >
        <span class="count-chip">{{ batchDisplayText }}</span>
      </div>
      <button
        v-if="!isReadOnly"
        class="btn-add-lines"
        :disabled="!customerCode"
        :title="!customerCode ? 'Select a customer first' : ''"
        @click="openLookup"
      >
        <svg viewBox="0 0 24 24">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add Lines…
      </button>
    </div>

    <dx-data-grid
      ref="linesGrid"
      :data-source="localLines"
      key-expr="_id"
      :height="getGridHeight"
      :show-borders="true"
      :word-wrap-enabled="true"
      :show-row-lines="true"
      :no-data-text="noDataText"
      :column-auto-width="false"
      :row-alternation-enabled="true"
      :column-chooser="{ enabled: false }"
      :pager="{ visible: false }"
      :columns="getColumns"
      :scrolling="{
        mode: 'virtual',
        rowRenderingMode: 'virtual',
        preloadEnabled: true,
      }"
      @toolbar-preparing="onToolbarPreparing"
      @content-ready="onContentReady"
      @row-updated="onDataChanged"
      @row-removed="onDataChanged"
    >
      <dx-editing
        :allow-updating="!isReadOnly"
        :allow-deleting="!isReadOnly"
        mode="cell"
        new-row-position="last"
      />
      <dx-paging :enabled="false" />
      <template #Record="{ data }">
        <GcRecord
          :data="data"
          :primary="data.column['cellTemplateRecord']['primary']"
          :secondary="data.column['cellTemplateRecord']['secondary']"
        />
      </template>
      <template #complaintTypeEditor="{ data }">
        <dx-select-box
          :data-source="complaintTypeItems"
          :value="data.value"
          :display-expr="
            function (item) {
              return item ? item.Code + ' - ' + item.Description : '';
            }
          "
          :search-expr="['Code', 'Description']"
          value-expr="Code"
          :search-enabled="true"
          :show-clear-button="false"
          :drop-down-options="{ width: 380, wrapperAttr: { class: 'complaint-lookup-dropdown' } }"
          item-template="cti"
          @value-changed="data.setValue($event.value)"
        >
          <template #cti="{ data: item }">
            <div class="complaint-lookup-item">
              <span class="complaint-lookup-primary">{{
                item.Description
              }}</span>
              <span class="complaint-lookup-secondary">{{ item.Code }}</span>
            </div>
          </template>
        </dx-select-box>
      </template>
      <template #rootCauseEditor="{ data }">
        <dx-select-box
          :data-source="rootCauseItems"
          :value="data.value"
          :display-expr="
            function (item) {
              return item ? item.Code + ' - ' + item.Description : '';
            }
          "
          :search-expr="['Code', 'Description']"
          value-expr="Code"
          :search-enabled="true"
          :show-clear-button="false"
          :drop-down-options="{ width: 380, wrapperAttr: { class: 'complaint-lookup-dropdown' } }"
          item-template="rci"
          @value-changed="data.setValue($event.value)"
        >
          <template #rci="{ data: item }">
            <div class="complaint-lookup-item">
              <span class="complaint-lookup-primary">{{
                item.Description
              }}</span>
              <span class="complaint-lookup-secondary">{{ item.Code }}</span>
            </div>
          </template>
        </dx-select-box>
      </template>
    </dx-data-grid>

    <dx-popup
      v-if="!isReadOnly"
      :visible.sync="lookupVisible"
      :drag-enabled="true"
      :close-on-outside-click="false"
      :show-title="true"
      title="Select Despatch Lines"
      width="90%"
      height="80%"
      :show-close-button="false"
      :wrapper-attr="{
        class: 'css-v2 gc-popup gc-popup-complaint-lines-lookup',
      }"
    >
      <dx-toolbar-item
        v-for="(item, index) in lookupToolbarItems"
        :key="index"
        :widget="item.widget"
        :location="item.location"
        :visible="item.visible"
        :toolbar="item.toolbar"
        :options="item.options"
      />
      <div class="css-v2 transaction-list">
        <gc-filter-split-view
          :showLeftContainer="true"
          :showRightContainer="showLookupRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <dx-data-grid
              v-if="lookupVisible"
              :column-chooser="{ enabled: false }"
              :pager="{ visible: false }"
              :columns="lookupGridColumns"
              :data-source="lookupDataSource"
              :row-alternation-enabled="true"
              :selection="{ mode: 'multiple' }"
              :height="lookupGridHeight"
              :width="'100%'"
              @toolbar-preparing="onLookupToolbarPreparing"
              @row-prepared="onLookupRowPrepared"
              @selection-changed="onLookupSelectionChanged"
              @content-ready="onLookupContentReady"
              ref="lookup-grid"
            >
              <dx-selection
                :select-all-mode="'allPages'"
                :show-check-boxes-mode="'always'"
                mode="multiple"
                :allow-select-all="true"
              />
              <dx-search-panel :visible="false" />
              <dx-paging :page-size="50" :enabled="true" />
              <dx-scrolling mode="virtual" row-rendering-mode="virtual" />

              <template #Record="{ data }">
                <GcRecord
                  :data="data"
                  :primary="data.column['cellTemplateRecord']['primary']"
                  :secondary="data.column['cellTemplateRecord']['secondary']"
                />
              </template>
              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>
              <template #Status="{ data }">
                <GcStatusChip
                  v-if="data.data[data.column.dataField]"
                  :status="data.data[data.column.dataField]"
                />
              </template>
            </dx-data-grid>
          </template>
          <template #rightContent>
            <gc-filter-panel
              v-if="lookupVisible"
              :toolbar-visible="true"
              :toolbar="lookupFilterToolbar"
              :form-item-data="lookupFilterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :grid-foolter="false"
              :from="'model'"
              :is-normal="false"
              :persistence-key="undefined"
              :over-write-clear="true"
              ref="lookup-filter-panel"
              :height-adjustment="lookupFilterHeightAdjustment"
            />
          </template>
        </gc-filter-split-view>
      </div>
    </dx-popup>
  </div>
</template>

<script>
import DxDataGrid, { DxEditing, DxPaging } from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import DxSelectBox from "devextreme-vue/select-box";
import { mapGetters } from "vuex";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { ComplaintTypeService } from "@/_services/api/settings/quality-control/complaint-type.service";
import { RootCauseClassificationService } from "@/_services/api/settings/quality-control/root-cause-classification.service";
import ApiService from "@/_services/api/api.service";
import appConfig from "@/app-config";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";

ApiService.init(appConfig.VUE_APP_ROOT_API);

export default {
  components: {
    DxDataGrid,
    DxEditing,
    DxPaging,
    DxPopup,
    DxSelectBox,
  },
  mixins: [DataGridMixin],

  props: {
    lines: {
      type: Array,
      required: true,
    },
    customerCode: {
      type: String,
      default: null,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    complaintTypeCode: {
      type: String,
      default: null,
    },
    rootCauseCode: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      localLines: [],
      complaintTypeItems: [],
      rootCauseItems: [],
      headerFormHeight: 0,
      linesHeaderHeight: 0,
      dataSourceType: "Core",
      defaultFilterValues: [],
      lookupVisible: false,
      lookupInitiateDataLoading: false,
      lookupSelectedItems: [],
      showLookupRightContainer: true,
      lookupGridHeight: "auto",
      lookupFilterHeightAdjustment: 0,
      lookupFilterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showLookupRightContainer = !this.showLookupRightContainer;
          },
        },
      },
    };
  },

  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),

    getGridHeight() {
      if (!this.gridHeight) return undefined;
      return (
        this.gridHeight - 167 - this.headerFormHeight - this.linesHeaderHeight
      );
      // 167 = sum of fixed heights between the scroll-view and the grid:
      //   10  transaction-screen padding-top
      //   55  gc-header-toolbar (45px content + 10px padding-bottom)
      //   10  complaint-content-tabs margin-top
      //   40  complaint-tab-toolbar
      //   16  complaint-tab-content padding-top
      //   10  lines-tab-header margin-bottom
      //   16  complaint-tab-content padding-bottom
      //   10  transaction-screen padding-bottom
    },

    uniqueProductCount() {
      var codes = {};
      this.localLines.forEach(function (l) {
        if (l.ProductCode) codes[l.ProductCode] = true;
      });
      return Object.keys(codes).length;
    },

    batchDisplayText() {
      var uniqueBatches = {};
      this.localLines.forEach(function (l) {
        if (l.Batch) {
          uniqueBatches[l.Batch] = true;
        }
      });
      var count = Object.keys(uniqueBatches).length;
      if (count === 0) return "0 batches";
      if (count === 1) return "1 batch";
      return count + " batches";
    },

    noDataText() {
      return this.isReadOnly
        ? "No product lines recorded."
        : "No lines yet — click Add Lines… to begin.";
    },

    getColumns() {
      var self = this;
      return this.parseColumnWithScreenFields([
        {
          screenField: "Product",
          dataField: "ProductCode",
          caption: "Product",
          cellTemplateRecord: {
            primary: [{ dataField: "ProductDescription" }],
            secondary: [{ dataField: "ProductCode" }],
          },
          allowEditing: false,
        },
        {
          dataField: "DeliveryNote",
          caption: "Delivery #",
          allowEditing: false,
          alignment: "left",
          width: ColumnWidth.DeliveryNumber.width,
          minWidth: ColumnWidth.DeliveryNumber.minWidth,
        },
        {
          dataField: "DeliveryNoteLine",
          caption: "Line #",
          alignment: "left",
          allowEditing: false,
          width: 50,
          minWidth: 50,
        },
        {
          dataField: "InvoiceNo",
          caption: "Invoice #",
          allowEditing: false,
          alignment: "left",
          width: ColumnWidth.InvoiceNumber.width,
          minWidth: ColumnWidth.InvoiceNumber.minWidth,
        },
        {
          dataField: "Batch",
          caption: "Batch",
          allowEditing: false,
          width: ColumnWidth.BatchNo.width,
          minWidth: ColumnWidth.BatchNo.minWidth,
        },
        {
          dataField: "WorkOrder",
          caption: "Work Order",
          allowEditing: false,
          width: ColumnWidth.WorkOrder.width,
          minWidth: ColumnWidth.WorkOrder.minWidth,
        },
        {
          dataField: "PackingLine",
          caption: "Packing Line",
          allowEditing: false,
          width: 100,
        },
        {
          dataField: "ComplaintTypeCode",
          caption: "Complaint Type",
          width: 160,
          validationRules: [
            { type: "required", message: "Complaint Type is required" },
          ],
          cssClass: "edit-cell-textbox-field",
          editCellTemplate: "complaintTypeEditor",
          lookup: {
            dataSource: self.complaintTypeItems,
            displayExpr: function (item) {
              return item ? item.Code + " - " + item.Description : "";
            },
            valueExpr: "Code",
          },
        },
        {
          dataField: "RootCauseClassificationCode",
          caption: "Root Cause",
          width: 160,
          cssClass: "edit-cell-textbox-field",
          editCellTemplate: "rootCauseEditor",
          lookup: {
            dataSource: self.rootCauseItems,
            displayExpr: function (item) {
              return item ? item.Code + " - " + item.Description : "";
            },
            valueExpr: "Code",
          },
        },
        {
          dataField: "Description",
          caption: "Description",
          editorType: "dxTextArea",
          editorOptions: {
            maxLength: 1000,
          },
          cssClass: "gc-editable-cell-color",
          validationRules: [
            {
              type: "stringLength",
              max: 1000,
              message: "Description cannot exceed 1000 characters",
            },
          ],
        },
        {
          type: "buttons",
          width: 30,
          visible: !self.isReadOnly,
          buttons: [{ name: "delete" }],
        },
      ]);
    },

    lookupToolbarItems() {
      var self = this;
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "Select",
            stylingMode: "contained",
            type: "success",
            disabled: self.lookupSelectedItems.length === 0,
            onClick: self.addSelectedLines,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "Cancel",
            stylingMode: "outlined",
            type: "danger",
            onClick: self.closeLookup,
          },
        },
      ];
    },

    lookupGridColumns() {
      return this.parseColumnWithScreenFields([
        {
          screenField: "Product",
          caption: "Product",
          dataField: "ProductCode",
          allowSorting: true,
          cellTemplateRecord: {
            primary: [{ dataField: "ProdDesc1" }],
            secondary: [{ dataField: "ProductCode" }],
          },
        },
        {
          caption: "Delivery #",
          dataField: "DeliveryNote",
          width: ColumnWidth.DeliveryNumber.width,
          minWidth: ColumnWidth.DeliveryNumber.minWidth,
          alignment: "left",
          allowSorting: true,
        },
        {
          caption: "Line #",
          dataField: "DeliveryNoteLine",
          width: 50,
          minWidth: 50,
          alignment: "left",
          allowSorting: false,
        },
        {
          caption: "Invoice",
          dataField: "InvoiceNo",
          width: 100,
          alignment: "left",
          allowSorting: true,
        },
        {
          screenField: "Batch",
          caption: "Batch",
          dataField: "Batch",
          width: 110,
          allowSorting: true,
        },
        {
          caption: "Work Order",
          dataField: "WorkOrder",
          width: 110,
          alignment: "left",
          allowSorting: true,
        },
        {
          caption: "Packing Line",
          dataField: "ProcessName",
          width: 120,
          allowSorting: true,
        },
        {
          screenField: "ShipDate",
          caption: "Despatch Date",
          dataField: "DespatchDate",
          sortOrder: "desc",
          sortIndex: 0,
          allowSorting: true,
          minWidth: ColumnWidth.Date.minWidth + 10,
          width: ColumnWidth.Date.width + 10,
          format: "dd MMM yyyy",
        },
      ]);
    },

    originalLookupDataSource() {
      var endpoint =
        this.dataSourceType === "Almondco"
          ? "api/AlmondcoComplaintDeliveredLine"
          : "api/ComplaintDeliveredLine";
      return ApiService.createDataSource(endpoint, {
        key: ["DeliveryNote", "DeliveryNoteLine"],
        sort: [{ selector: "DespatchDate", desc: true }],
        filter: this.customerCode
          ? [["CustomerCode", "=", this.customerCode]]
          : undefined,
        pageSize: 50,
      });
    },

    lookupDataSource() {
      if (!this.customerCode || !this.lookupInitiateDataLoading) return [];
      return this.originalLookupDataSource;
    },

    alreadyAddedKeys() {
      var result = {};
      this.localLines.forEach(function (l) {
        var key =
          (l.DeliveryNote || "") +
          "-" +
          (l.DeliveryNoteLine || "") +
          "-" +
          (l.InvoiceNo || "") +
          "-" +
          (l.ProductCode || "") +
          "-" +
          (l.Batch || "") +
          "-" +
          (l.WorkOrder || "");
        result[key] = true;
      });
      return result;
    },

    lookupFilterFormItems() {
      return [
        {
          screenField: "ShipDate",
          filterLabel: "Despatch Date",
          filterDataField: "DespatchDate",
          filterOperator: ">=",
          filterDefaultValue: new Date().addDays(-180),
          type: "Date",
        },
        {
          screenField: "Product",
          filterLabel: "Product",
          filterDataField: "ProductCode",
        },
        {
          screenField: "CustPO",
          filterLabel: "Delivery #",
          filterDataField: "DeliveryNote",
          type: "Integer",
        },
        {
          screenField: "CustPO",
          filterLabel: "Invoice No",
          filterDataField: "InvoiceNo",
          type: "Integer",
        },
        {
          screenField: "Code",
          filterLabel: "Work Order",
          filterDataField: "WorkOrder",
          type: "Integer",
        },
        {
          screenField: "CustPO",
          filterLabel: "Batch",
          filterDataField: "Batch",
          type: "Text",
        },
      ];
    },
  },

  async created() {
    this._idCounter = 1;
    var self = this;
    self.localLines = (self.lines || []).map(function (l) {
      return Object.assign({}, l, {
        _id: self._idCounter++,
        IsBatchControlled:
          l.IsBatchControlled !== undefined ? l.IsBatchControlled : !!l.Batch,
        IsWorkOrderLinked:
          l.IsWorkOrderLinked !== undefined
            ? l.IsWorkOrderLinked
            : !!l.WorkOrder,
        _deliveryNote: l._deliveryNote !== undefined ? l._deliveryNote : null,
        _deliveryNoteLine:
          l._deliveryNoteLine !== undefined ? l._deliveryNoteLine : null,
      });
    });

    await Promise.all([
      self.loadDataSourceType(),
      self.loadComplaintTypes(),
      self.loadRootCauses(),
    ]);
  },

  mounted() {
    this.$nextTick(
      function () {
        var headerForm = document.querySelectorAll(".gc-header-form");
        if (headerForm[0]) {
          this.headerFormHeight = headerForm[0].clientHeight;
        }
        var linesHeader = this.$el.querySelector(".lines-tab-header");
        if (linesHeader) {
          this.linesHeaderHeight = linesHeader.clientHeight;
        }
      }.bind(this),
    );
  },

  methods: {
    commitEdits() {
      return this.$refs.linesGrid.instance.saveEditData();
    },

    applyComplaintType(code) {
      this.localLines.forEach(function (l) {
        l.ComplaintTypeCode = code;
      });
      this.onDataChanged();
    },

    onToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    onContentReady(e) {
      this.addGridNoResults(e, undefined);
    },

    async loadDataSourceType() {
      try {
        var response = await ApiService.get(
          "api/ApplicationFeatureSetting?$filter=ApplicationFeatureCode eq 'CustomerComplaints' and SettingName eq 'DeliveredLinesDataSourceType'&$top=1",
        );
        var items =
          response && response.data && response.data.value
            ? response.data.value
            : [];
        if (items.length > 0 && items[0].SettingValue) {
          this.dataSourceType = items[0].SettingValue;
        }
      } catch (e) {
        this.dataSourceType = "Core";
      }
    },

    async loadComplaintTypes() {
      try {
        var response = await ComplaintTypeService.getAll();
        this.complaintTypeItems =
          response && response.data && response.data.value
            ? response.data.value
            : [];
      } catch (e) {
        this.complaintTypeItems = [];
      }
    },

    async loadRootCauses() {
      try {
        var response = await RootCauseClassificationService.getAll();
        this.rootCauseItems =
          response && response.data && response.data.value
            ? response.data.value
            : [];
      } catch (e) {
        this.rootCauseItems = [];
      }
    },

    openLookup() {
      if (!this.customerCode) return;
      this.lookupSelectedItems = [];
      this.defaultFilterValues = [];
      this.lookupInitiateDataLoading = false;
      this.lookupVisible = true;
      this.$nextTick(function () {
        setTimeout(
          function () {
            var el = document.querySelectorAll(
              ".gc-popup-complaint-lines-lookup .dx-popup-content",
            )[0];
            if (el) {
              this.lookupGridHeight = this.lookupFilterHeightAdjustment =
                el.clientHeight - 20;
            }
          }.bind(this),
          1,
        );
      });
    },

    closeLookup() {
      this.lookupVisible = false;
      this.lookupSelectedItems = [];
    },

    onLookupToolbarPreparing(e) {
      e.toolbarOptions.items.push({
        location: "after",
        locateinmenu: "auto",
        widget: "dxButton",
        options: {
          icon: "filter",
          onClick: () => {
            this.showLookupRightContainer = !this.showLookupRightContainer;
          },
          visible: !this.showLookupRightContainer,
        },
      });
    },

    isAlreadyAdded(rowData) {
      var key =
        (rowData.DeliveryNote || "") +
        "-" +
        (rowData.DeliveryNoteLine || "") +
        "-" +
        (rowData.InvoiceNo || "") +
        "-" +
        (rowData.ProductCode || "") +
        "-" +
        (rowData.Batch || "") +
        "-" +
        (rowData.WorkOrder || "");
      return !!this.alreadyAddedKeys[key];
    },

    onLookupRowPrepared(e) {
      if (e.rowType === "data" && this.isAlreadyAdded(e.data)) {
        e.rowElement.classList.add("row-already-added");
      }
    },

    onLookupSelectionChanged(e) {
      var self = this;
      var alreadyAddedRowKeys = e.selectedRowsData
        .filter(function (row) {
          return self.isAlreadyAdded(row);
        })
        .map(function (row) {
          return {
            DeliveryNote: row.DeliveryNote,
            DeliveryNoteLine: row.DeliveryNoteLine,
          };
        });
      if (alreadyAddedRowKeys.length > 0) {
        e.component.deselectRows(alreadyAddedRowKeys);
      }
      this.lookupSelectedItems = e.selectedRowsData.filter(function (row) {
        return !self.isAlreadyAdded(row);
      });
    },

    onLookupContentReady(e) {
      this.addGridNoResults(e, undefined);
    },

    setGridPanelFilter(e, _valueChanged, applyButtonLabel) {
      var dataGrid = this.$refs["lookup-grid"].instance;
      var customerFilter = this.customerCode
        ? ["CustomerCode", "=", this.customerCode]
        : null;

      if (
        e.length === 0 &&
        !applyButtonLabel &&
        this.defaultFilterValues.length > 0
      ) {
        // Reset button (no applyButtonLabel): re-apply CustomerCode + stored panel defaults
        var defFilter =
          this.defaultFilterValues.length === 1
            ? this.defaultFilterValues[0]
            : this.defaultFilterValues;
        dataGrid.filter(customerFilter ? [customerFilter, "and", defFilter] : defFilter);
        dataGrid.refresh(true);
      } else {
        if (this.lookupInitiateDataLoading === false) {
          dataGrid.option("dataSource", this.originalLookupDataSource);
          this.lookupInitiateDataLoading = true;
          if (e.length > 0) this.defaultFilterValues = [...e];
        }

        if (dataGrid) {
          if (e.length > 0) {
            var panelFilter = e.length === 1 ? e[0] : e;
            dataGrid.filter(
              customerFilter ? [customerFilter, "and", panelFilter] : panelFilter,
            );
          } else {
            if (customerFilter) dataGrid.filter([customerFilter]);
            else dataGrid.clearFilter();
          }
          dataGrid.refresh(true);
        }
      }
    },

    addSelectedLines() {
      var self = this;
      this.lookupSelectedItems.forEach(function (item) {
        var newLine = {
          _id: self._idCounter++,
          RecordId: null,
          ProductCode: item.ProductCode || "",
          ProductDescription: item.ProdDesc1 || "",
          DeliveryNote: item.DeliveryNote || "",
          DeliveryNoteLine: item.DeliveryNoteLine || "",
          InvoiceNo: item.InvoiceNo || "",
          Batch: item.Batch || "",
          WorkOrder: item.IsWorkOrderLinked ? String(item.WorkOrder || "") : "",
          PackingLine: item.ProcessName || "",
          ComplaintTypeCode: self.complaintTypeCode || null,
          RootCauseClassificationCode: self.rootCauseCode || null,
          Description: "",
          IsBatchControlled: !!(item.IsBatchControlled || item.Batch),
          IsWorkOrderLinked: !!item.IsWorkOrderLinked,
        };
        self.localLines.push(newLine);
      });
      this.onDataChanged();
      this.closeLookup();
    },

    onDataChanged() {
      var self = this;
      self.lines.splice(0, self.lines.length);
      self.localLines.forEach(function (l) {
        self.lines.push({
          RecordId: l.RecordId || null,
          ProductCode: l.ProductCode || "",
          ProductDescription: l.ProductDescription || "",
          DeliveryNote: l.DeliveryNote || "",
          DeliveryNoteLine: l.DeliveryNoteLine || "",
          InvoiceNo: l.InvoiceNo || "",
          Batch: l.Batch || "",
          WorkOrder: l.WorkOrder || "",
          PackingLine: l.PackingLine || "",
          ComplaintTypeCode: l.ComplaintTypeCode || null,
          RootCauseClassificationCode: l.RootCauseClassificationCode || null,
          Description: l.Description || "",
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dx-card {
  box-shadow: none;
  -webkit-box-shadow: none;
}

.lines-tab {
  .lines-tab-header {
    display: flex;
    align-items: center;
    padding: 0;
    margin-bottom: 10px;

    .lines-counts {
      display: flex;
      gap: 6px;
      align-items: center;
    }

    .btn-add-lines {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 12px;
      border-radius: 2px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      background: #35baf6;
      border: 1px solid #2a95c5;
      color: #fff;
      transition: background 0.1s;
      user-select: none;

      &:hover:not(:disabled) {
        background: #2aa8e0;
      }

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      svg {
        width: 13px;
        height: 13px;
        stroke: currentColor;
        fill: none;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        flex-shrink: 0;
      }
    }
  }

  .count-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #667085;
    background: #f9fafb;
    border: 1px solid #eaecf0;
    border-radius: 9999px;
    padding: 2px 10px;
    white-space: nowrap;
  }

  .nil-val {
    color: #d0d5dd;
    font-size: 12px;
  }
}
</style>

<style lang="scss">
/* Non-scoped: targets DevExtreme internals that render outside scoped DOM */
.lines-tab .dx-datagrid-header-panel {
  display: none;
}

.complaint-lookup-item {
  line-height: 1.4;

  .complaint-lookup-primary {
    font-size: 13px;
    color: #101828;
  }

  .complaint-lookup-secondary {
    font-size: 11px;
    color: #667085;
    margin-left: 6px;
  }
}

/* Target the SelectBox popup wrapper that DevExtreme always adds to SelectBox dropdowns.
   The ::before strut (float:left inline-block "_") creates a blank line above our flex div;
   suppressing it removes the extra top space. */
.dx-selectbox-popup-wrapper .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  height: auto !important;
  padding: 10px!important;
}

.dx-selectbox-popup-wrapper .dx-list-item-content::before {
  display: none !important;
}

/* Textarea editor must not expand the row — position it absolutely so it
   fills the cell visually without contributing to the row height calculation */
.lines-tab .dx-editor-cell {
  position: relative;
}

.lines-tab .dx-editor-cell .dx-textarea {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  .dx-texteditor-container,
  .dx-texteditor-input-container {
    height: 100%;
  }

  .dx-texteditor-input {
    height: 100% !important;
    overflow-y: auto !important;
    resize: none;
    white-space: pre-wrap;
    font-size: 12px;
  }
}

.gc-popup-complaint-lines-lookup {
  .dx-datagrid .dx-row.row-already-added {
    opacity: 0.45;
    pointer-events: none;
    cursor: not-allowed;
  }

  .advance-filter-panel .dx-form {
    padding-bottom: 10px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:first-of-type:not(.dx-command-expand) {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
}

.lines-tab tr td {
  vertical-align: top !important;
}
</style>
